import React from 'react';
import PropTypes from 'prop-types';
import {
  Breadcrumb,
  ProgressIndicator,
  Modal,
  RewardReviewSkeletonLoader
} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {ROUTES} from '@/utils/constants/routesConstants';
import RewardDownload from '@/containers/Rewards/RewardReview/components/RewardDownload';

/**
 * Shared wrapper component for all reward flows (Create, Edit, View)
 * Handles common layout, breadcrumbs, progress indicator, modals, and footer
 */
const RewardFlowWrapper = ({
  mode = 'create',
  title,
  breadcrumbLabel,
  showProgressIndicator = true,
  showFooter = true,
  footerProps = {},
  onCancel,
  onNext,
  onDraft,
  isLoading = false,
  formErrors = false,
  currentStep = 1,
  stepTitles = [],
  onStepClick,
  isEditDisabled = false,
  showDownload = false,
  updatedDate,
  isNameValid = true,
  onNameValidChange,
  isCancel = false,
  onCancelConfirm,
  onCancelReject,
  children
}) => {
  const isReviewStep = currentStep === 4;
  const isReadOnlyMode = mode === 'view';

  if (isLoading) {
    return <RewardReviewSkeletonLoader />;
  }

  return (
    <div className='flex flex-col gap-6'>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: ''
            },
            {
              href: ROUTES.REWARD_CATALOG,
              label: 'Reward Catalog'
            },
            {
              href: '',
              label: breadcrumbLabel || title
            }
          ]
        }}
        id={'create_reward_breadcrumb'}
      />

      <PageHeader wrapperClassName='mt-0 h-12' title={title} />

      {showProgressIndicator ? (
        <ProgressIndicator
          currentStep={currentStep}
          completed={false}
          steps={4}
          titles={stepTitles}
          isDark={true}
          className='p-6'
          onStepClick={
            isEditDisabled || isReadOnlyMode ? undefined : onStepClick
          }
        />
      ) : (
        showDownload && <RewardDownload updatedDate={updatedDate} />
      )}

      <div className='step-content'>{children}</div>

      {!isNameValid && (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={!isNameValid}
          title='Duplicate Name Detected'
          text={
            <>
              It looks like there's already a Reward with this name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          }
          showPrimaryButton={true}
          primaryButtonText='Edit Name'
          onClickPrimaryButton={() => onNameValidChange?.(true)}
          showSecondaryButton={false}
          onCloseModal={() => onNameValidChange?.(true)}
          footerProps={{
            size: 'medium',
            type: 'primary'
          }}
          isDark={true}
          hideCloseButton={true}
        />
      )}

      {isCancel && (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={onCancelReject}
          title='Are you sure you want to cancel?'
          text={
            'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
          }
          showPrimaryButton={true}
          primaryButtonText='Yes'
          onClickPrimaryButton={onCancelConfirm}
          showSecondaryButton={true}
          secondaryButtonText='No'
          onClickSecondaryButton={onCancelReject}
          footerProps={{
            size: 'medium',
            type: 'primary'
          }}
          isDark={true}
        />
      )}

      {showFooter && (
        <div className='flex h-[144px] items-end'>
          <SaveCancelFooter
            handleCancel={onCancel}
            nextAction={onNext}
            draftAction={onDraft}
            FormError={formErrors}
            isReview={isReviewStep}
            disableDraft={footerProps.disableDraft}
            wrapperClassName='w-full'
          />
        </div>
      )}
    </div>
  );
};

RewardFlowWrapper.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit', 'view']),
  title: PropTypes.string.isRequired,
  breadcrumbLabel: PropTypes.string,
  showProgressIndicator: PropTypes.bool,
  showFooter: PropTypes.bool,
  footerProps: PropTypes.object,
  onCancel: PropTypes.func,
  onNext: PropTypes.func,
  onDraft: PropTypes.func,
  isLoading: PropTypes.bool,
  formErrors: PropTypes.bool,
  currentStep: PropTypes.number,
  stepTitles: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string
    })
  ),
  onStepClick: PropTypes.func,
  isEditDisabled: PropTypes.bool,
  showDownload: PropTypes.bool,
  updatedDate: PropTypes.string,
  isNameValid: PropTypes.bool,
  onNameValidChange: PropTypes.func,
  isCancel: PropTypes.bool,
  onCancelConfirm: PropTypes.func,
  onCancelReject: PropTypes.func,
  children: PropTypes.node.isRequired
};

export default RewardFlowWrapper;
